import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import Loader from "../../components/elements/Loader";
import SnackbarAlert from "../../components/elements/SnackbarAlert";
import ListOfTripRequest from "../../screens/ListOfTripRequest/ListOfTripRequest";
import "./css/TripCard.css";
import FilterComponent from "./views/FilterComponent";
import Header from "./views/Header";
import PaginationComponent from "./views/PaginationComponent";
import SearchBar from "./views/SearchBar";
import useLandingAuthApi from "../../services/api/useLandingAuthApi";
import ROUTE_PATHS from "../../utils/routePaths";
import { getServiceUrls } from "../../services/serviceUrls";
import VersioningComponent from "./views/VersioningComponent";
import ErrorPopup from "../../components/elements/ErrorPopup";
import { TRIP_REQUEST_SEARCH_TERM_KEY } from "../../utils/searchStorage";
import {
  TRIP_REQUEST_TAB,
  TRIP_REQUEST_TAB_CREATED_BY_ADSB,
} from "../../utils/constants";

const downloadUpcomingReportFile = (data, filename = "upcoming-trips.xlsx") => {
  const url = window.URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const getUpcomingExportFilename = (filters) => {
  const datePart = filters?.selectedDateFrom
    ? (filters.selectedDateFrom.format
      ? filters.selectedDateFrom.format("YYYY-MM-DD")
      : filters.selectedDateFrom)
    : dayjs().format("YYYY-MM-DD");
  const timePart = dayjs().format("HHmmss");
  return `upcoming-trips-${datePart}-${timePart}.xlsx`;
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const buildExportDataForUpcoming = (filters, selectedTab, searchTerm) => {
  const data = {};
  if (filters?.selectedArrivalAirport) {
    data.arrivalAirportId = filters.selectedArrivalAirport;
  }
  if (filters?.selectedDepartureAirport) {
    data.departureAirportId = filters.selectedDepartureAirport;
  }
  if (filters?.selectedDateFrom) {
    data.departureDate = filters.selectedDateFrom.format
      ? filters.selectedDateFrom.format("YYYY-MM-DD")
      : filters.selectedDateFrom;
  }
  if (filters?.registrationNumber?.trim()) {
    data.registrationNumber = filters.registrationNumber.trim();
  }
  const trimmedSearchTerm = (searchTerm || "").trim();
  if (isValidEmail(trimmedSearchTerm)) {
    data.email = trimmedSearchTerm;
  }
  const createdByAdsb = TRIP_REQUEST_TAB_CREATED_BY_ADSB[selectedTab];
  if (createdByAdsb !== undefined) {
    data.createdByAdsb = createdByAdsb;
  }
  return data;
};

const TripRequestPage = () => {
  const {
    fetchAllTrips,
    searchByTrackingId,
    fetchTripByFilters,
    fetchAllOverTimeTrips,
    fetchAllEscortTrips,
    exportUpcomingTrips,
  } = useLandingAuthApi();

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const initialPage =
    parseInt(location.state?.currentPage) ||
    parseInt(searchParams.get("page")) ||
    1;
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem(TRIP_REQUEST_SEARCH_TERM_KEY) || ""
  );
  const itemsPerPage = 10;
  const [activeFilters, setActiveFilters] = useState(null);
  const [selectedTab, setSelectedTab] = useState(
    localStorage.getItem("selectedTripRequestTab") || TRIP_REQUEST_TAB.ALL
  );
  const navigate = useNavigate();
  const SERVICE_URLS = getServiceUrls();
  const [errorMessage, setErrorMessage] = useState("");
  const [showExportSuccessMessage, setShowExportSuccessMessage] = useState(false);

  const downloadUpcomingMutation = useMutation({
    mutationFn: (filters) => {
      const exportData = buildExportDataForUpcoming(
        filters,
        selectedTab,
        searchTerm
      );
      return exportUpcomingTrips(exportData);
    },
    onSuccess: (response, filters = {}) => {
      if (response) {
        downloadUpcomingReportFile(response, getUpcomingExportFilename(filters));
      }
      setShowExportSuccessMessage(true);
      setTimeout(() => setShowExportSuccessMessage(false), 4000);
    },
  });

  const {
    data: tripData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      activeFilters ? "filters-trips" : "trips",
      currentPage,
      itemsPerPage,
      searchTerm.length > 3 ? searchTerm : "list",
      activeFilters,
      selectedTab,
    ],
    queryFn: async () => {
      if (searchTerm && searchTerm.length > 3) {
        try {
          const result = await searchByTrackingId(searchTerm);
          setErrorMessage("");
          return result;
        } catch (error) {
          if (error.status === 404) {
            setErrorMessage("");
            return null;
          }
          setErrorMessage(
            error.message ||
            "An error occurred while searching for trips. Please try again."
          );
          throw error;
        }
      }

      if (activeFilters) {
        try {
          const filterData = {
            page: currentPage - 1,
            size: itemsPerPage,
            arrivalAirportId: activeFilters.selectedArrivalAirport || "",
            departureAirportId: activeFilters.selectedDepartureAirport || "",
            departureDate: activeFilters.selectedDateFrom
              ? activeFilters.selectedDateFrom.format("YYYY-MM-DD")
              : "",
            registrationNumber:
              activeFilters.registrationNumber?.trim() || "",
          };
          const createdByAdsb = TRIP_REQUEST_TAB_CREATED_BY_ADSB[selectedTab];
          if (createdByAdsb !== undefined) {
            filterData.createdByAdsb = createdByAdsb;
          }
          const result = await fetchTripByFilters(filterData);
          setErrorMessage("");
          return result;
        } catch (error) {
          setErrorMessage(
            error.message ||
            "An error occurred while filtering trips. Please try again."
          );
          throw error;
        }
      }

      if (TRIP_REQUEST_TAB_CREATED_BY_ADSB[selectedTab] !== undefined) {
        try {
          const filterData = {
            page: currentPage - 1,
            size: itemsPerPage,
            createdByAdsb: TRIP_REQUEST_TAB_CREATED_BY_ADSB[selectedTab],
          };
          const result = await fetchTripByFilters(filterData);
          setErrorMessage("");
          return result;
        } catch (error) {
          setErrorMessage(
            error.message ||
            "An error occurred while fetching trips. Please try again."
          );
          throw error;
        }
      }

      try {
        // Use different API calls based on selected tab
        let result;
        switch (selectedTab) {
          case TRIP_REQUEST_TAB.AFTER_HOURS:
            result = await fetchAllOverTimeTrips(currentPage - 1, itemsPerPage);
            break;
          case TRIP_REQUEST_TAB.SPECIAL_ONBOARDING:
            result = await fetchAllEscortTrips(currentPage - 1, itemsPerPage);
            break;
          case TRIP_REQUEST_TAB.ALL:
          default:
            result = await fetchAllTrips(currentPage - 1, itemsPerPage);
        }
        setErrorMessage("");
        return result;
      } catch (error) {
        if (error.status !== 404) {
          return null;
        } else {
          setErrorMessage(
            error.message ||
            "An error occurred while fetching trips. Please try again."
          );
        }
        throw error;
      }
    },
    keepPreviousData: true,
    enabled: true,
    retry: false,
  });

  let trips = [];
  if (tripData) {
    if (searchTerm && searchTerm.length > 3 && tripData?.data) {
      trips = tripData.data?.content || [];
    } else if (tripData?.data) {
      trips = tripData.data?.content || [];
    }
  }

  const totalPages = tripData?.data?.totalPages || 1;
  const totalElements = tripData?.data?.totalElements || 0;
  const cumulativeCount = Math.min(currentPage * itemsPerPage, totalElements);

  const persistSearchTerm = (term) => {
    if (term) {
      localStorage.setItem(TRIP_REQUEST_SEARCH_TERM_KEY, term);
    } else {
      localStorage.removeItem(TRIP_REQUEST_SEARCH_TERM_KEY);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSearchParams({ page: page.toString() }, { replace: false });
  };

  const handleSearch = (searchText) => {
    if (searchText) {
      setActiveFilters(null);
      setCurrentPage(1);
    }
    setSearchTerm(searchText);
    persistSearchTerm(searchText);
  };

  const handleFilterApplied = (filters) => {
    setActiveFilters(filters);
    setCurrentPage(1);
    setSearchTerm("");
    persistSearchTerm("");
  };

  const handleFilterCleared = () => {
    setActiveFilters(null);
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    if (selectedTab !== tab) {
      localStorage.setItem("selectedTripRequestTab", tab);
      setSelectedTab(tab);
      setSearchTerm("");
      setActiveFilters(null);
      persistSearchTerm("");
      setCurrentPage(1);
      setSearchParams({ page: "1" }, { replace: true });
    }
  };

  const handlePageReset = () => {
    setCurrentPage(1);
    setSearchParams({ page: "1" }, { replace: true });
  };
  const isSearchEmailValid = isValidEmail((searchTerm || "").trim());
  const handleExportFromHeader = () => {
    if (!isSearchEmailValid || downloadUpcomingMutation.isPending) {
      return;
    }
    downloadUpcomingMutation.mutate(activeFilters || {});
  };

  const handleQrScanSuccess = async (trackingId) => {
    try {
      const result = await searchByTrackingId(trackingId);
      if (result?.data) {
        setErrorMessage("");
        navigate(ROUTE_PATHS.TRIP_DETAILS(result?.data?.id), {
          state: { currentPage: currentPage, tripId: result?.data?.id },
        });
      }
    } catch (error) {
      console.error("Error searching trip:", error);
      if (error.status !== 404) {
        setErrorMessage(error.message);
      }
      setSearchTerm("");
      persistSearchTerm("");
    }
  };

  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState(false);

  useEffect(() => {
    const pageFromUrl = searchParams.get("page");
    if (pageFromUrl) {
      const parsedPage = parseInt(pageFromUrl);
      if (parsedPage !== currentPage) {
        setCurrentPage(parsedPage);
      }
    } else if (location.state?.currentPage) {
      setCurrentPage(location.state.currentPage);
    } else {
      setSearchParams({ page: "1" }, { replace: true });
    }
  }, [location.search, location.state]);

  useEffect(() => {
    if (!searchParams.get("page") && currentPage > 1) {
      setSearchParams({ page: currentPage.toString() }, { replace: true });
    }
  }, [location.key]);
  return (
    <div className="app">
      <Header
        onExportRequest={handleExportFromHeader}
        isExportPending={downloadUpcomingMutation.isPending}
        isExportDisabled={!isSearchEmailValid}
      />
      <SearchBar
        onSearch={handleSearch}
        onQrScanSuccess={handleQrScanSuccess}
        value={searchTerm}
        placeholder="Search by Name, Email ID, or Trip ID"
      />
      <FilterComponent
        onFilterApplied={handleFilterApplied}
        onFilterCleared={handleFilterCleared}
        resetFilters={!!searchTerm}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        onPageReset={handlePageReset}
      />
      {error && error.status !== 404 && (
        <div className="trip-request-error-message">
          <ErrorPopup children={error.message}></ErrorPopup>
        </div>
      )}
      {error && error.status === 404 && (
        <div className="no-trips-message-container">No trips available</div>
      )}
      {!error && (
        <>
          <div className="trip-request-row">
            <div className="trip-request-label"></div>
            {trips.length > 0 && !searchTerm && !isLoading && tripData && (
              <div className="trip-result">
                <p>
                  {isLoading
                    ? "Loading..."
                    : `Showing ${cumulativeCount} of ${totalElements}`}
                </p>
              </div>
            )}
          </div>

          {isLoading && trips.length === 0 && <Loader />}
          {(!tripData || trips.length === 0) && !isLoading && (
            <div className="no-trips-message-container">No trips available</div>
          )}
          {trips.length > 0 && tripData && (
            <ListOfTripRequest trips={trips} currentPage={currentPage} />
          )}
        </>
      )}
      {showExportSuccessMessage && (
        <SnackbarAlert type="success">
          Upcoming trips report has been downloaded successfully.
        </SnackbarAlert>
      )}
      {downloadUpcomingMutation.isError && (
        <SnackbarAlert type="error">
          {downloadUpcomingMutation.error?.message ||
            "Failed to download upcoming trips report"}
        </SnackbarAlert>
      )}
      <div className="footer-container">
        {SERVICE_URLS.REACT_APP_VERSION && (
          <div className="version-info">
            <button
              onClick={() => setIsVersionDialogOpen(true)}
              className="version-button"
            >
              Version {SERVICE_URLS.REACT_APP_VERSION}
            </button>
            <VersioningComponent
              isOpen={isVersionDialogOpen}
              onClose={() => setIsVersionDialogOpen(false)}
              version={SERVICE_URLS.REACT_APP_VERSION}
            />
          </div>
        )}
        {!error && (
          <>
            {trips.length > 0 && totalPages > 1 && (
              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TripRequestPage;
